/**
 * Upload.jsx — v2 strangler swap (Item 3b of BACKLOG.md Great
 * Simplification).
 *
 * v2 contract differs from v1 in two ways that force a real rewrite:
 *   1. ``project_id`` is REQUIRED — a tender belongs to a project.
 *      Daily users never seeing this surface needed a frictionless
 *      picker so they don't fight the new requirement.
 *   2. Analysis runs INLINE inside the POST /rfp/upload response.
 *      The v1 WebSocket progress channel is gone; we render a clear
 *      "Analysing…" state and let the response land (typical 15-90s
 *      for a real RFP PDF). No fake progress bar.
 *
 * Atomic design — three internal pieces:
 *   ProjectPicker      pure UI; emits ``onChange(projectId)``
 *   FileDropZone       pure UI; emits ``onSelect(File)``
 *   UploadCard         orchestrates state machine + API calls
 *
 * Type-safety: no ``any``-typed payloads — every API call's response
 * is destructured into named fields, and we early-return on invalid
 * shapes (the directive's static-guarding rule).
 *
 * Error handling: every API call is wrapped in try/catch with a
 * user-actionable toast. We never silently swallow.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Upload as UploadIcon,
  FileText, FileSpreadsheet, FileImage,
  X, CheckCircle2, Loader2, AlertCircle, Plus, Eye, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";


// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

// Mirrors backend ALLOWED_SUFFIXES (see app/routers/rfp.py).
const ALLOWED_EXTS = [".pdf", ".docx", ".txt", ".md"];
const MAX_BYTES = 300 * 1024 * 1024;    // 300 MB; matches backend max_upload_mb default.

// Friendly metadata per MIME. Anything we accept maps here; anything else
// is rejected at the drop boundary with a clear message.
const TYPE_META = {
  "application/pdf":
    { icon: FileText, label: "PDF", color: "text-red-500" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    { icon: FileText, label: "DOCX", color: "text-blue-500" },
  "text/plain":
    { icon: FileText, label: "TXT", color: "text-slate-500" },
  "text/markdown":
    { icon: FileText, label: "MD", color: "text-slate-500" },
};

function suffix(name) {
  const i = (name || "").lastIndexOf(".");
  return i < 0 ? "" : name.slice(i).toLowerCase();
}

function isAcceptable(file) {
  if (!file) return false;
  if (file.size > MAX_BYTES) return false;
  return ALLOWED_EXTS.includes(suffix(file.name));
}

function rejectionReason(file) {
  if (!file) return "no file";
  if (file.size > MAX_BYTES) {
    return `file is ${(file.size / 1024 / 1024).toFixed(1)} MB — cap is ${MAX_BYTES / 1024 / 1024} MB`;
  }
  return `unsupported type ${suffix(file.name) || "(no extension)"} — accept ${ALLOWED_EXTS.join(", ")}`;
}

function fmtSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}


// ---------------------------------------------------------------------------
// Atomic UI: ProjectPicker
// ---------------------------------------------------------------------------

function ProjectPicker({ projects, value, onChange, onCreateProject, busy, isAdmin }) {
  const [openCreate, setOpenCreate] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [creating, setCreating] = useState(false);

  const submitNewProject = async () => {
    if (!code.trim() || !name.trim()) {
      toast.error("Project needs at least a code and a name");
      return;
    }
    setCreating(true);
    try {
      const created = await onCreateProject({
        code: code.trim(),
        name: name.trim(),
        client_name: clientName.trim() || null,
      });
      setOpenCreate(false);
      setCode("");
      setName("");
      setClientName("");
      onChange(created.id);
      // Inline create skips the 3-step wizard (sections + workflow gates)
      // that /v2/projects offers. Surface deeplinks so the user can
      // configure those without hunting through settings.
      toast.success(
        (t) => (
          <div className="text-xs">
            <p className="font-medium mb-1">Project {created.code} created</p>
            <p className="text-slate-600 mb-2">
              Configure section config and workflow gates for this project:
            </p>
            <div className="flex gap-2">
              <a
                href={`/v2/projects/${created.id}/section-config`}
                className="px-2 py-1 rounded border bg-white text-blue-600 hover:bg-blue-50"
              >
                Section config
              </a>
              <a
                href={`/v2/projects/${created.id}/workflow`}
                className="px-2 py-1 rounded border bg-white text-blue-600 hover:bg-blue-50"
              >
                Workflow gates
              </a>
            </div>
          </div>
        ),
        { duration: 12000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not create project");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Project
          </label>
          <div className="flex gap-2 mt-1 min-w-0">
            <select
              value={value || ""}
              onChange={(e) => onChange(e.target.value || null)}
              disabled={busy}
              className="flex-1 min-w-0 px-3 py-2 border rounded-md bg-white text-sm"
            >
              <option value="">— select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ""}{p.name}
                  {p.client_name ? ` (${p.client_name})` : ""}
                </option>
              ))}
            </select>
            {/* "+ New project" now deeplinks into the full 3-step wizard
                on /v2/projects. The wizard's "Done" navigates back to
                /upload?project=<new-id> so the user lands here with the
                new project preselected. The legacy inline mini-create
                stays available via the small "quick add" link below. */}
            {isAdmin() && (
              <>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => window.location.assign("/v2/projects?openCreate=1&returnTo=upload")}
                  disabled={busy}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> New project
                </Button>
                <button
                  type="button"
                  onClick={() => setOpenCreate(!openCreate)}
                  disabled={busy}
                  className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                  title="Skip the 3-step wizard; just create a stub project here"
                >
                  quick add
                </button>
              </>
            )}
          </div>
          {projects.length === 0 && !openCreate && (
            <p className="text-xs text-slate-500 mt-2">
              {isAdmin()
                ? <>No projects yet — every tender belongs to a project, so click <strong>New project</strong> to create one.</>
                : "No projects available. Ask a workspace admin to create one."}
            </p>
          )}
        </div>

        {openCreate && (
          <div className="border-t pt-3 grid grid-cols-12 gap-2 text-sm">
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Code *</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)}
                     placeholder="JJM-2026-01" disabled={creating} />
            </div>
            <div className="col-span-5">
              <label className="text-xs text-slate-500">Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="JJM Phase 4 MVS supply"
                     disabled={creating} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Client</label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)}
                     placeholder="PHED Madhya Pradesh"
                     disabled={creating} />
            </div>
            <div className="col-span-12 flex justify-end gap-2">
              <Button variant="ghost" size="sm"
                      onClick={() => setOpenCreate(false)}
                      disabled={creating}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitNewProject} disabled={creating}>
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Create & select
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Atomic UI: FileDropZone
// ---------------------------------------------------------------------------

function FileDropZone({ file, onSelect, busy }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (busy) return;
    const f = e.dataTransfer.files?.[0];
    onSelect(f || null);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    onSelect(f);
  };

  const meta = file ? TYPE_META[file.type] : null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!busy) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition cursor-pointer ${
        busy ? "bg-slate-50 border-slate-200 cursor-not-allowed" :
        dragging ? "bg-blue-50 border-blue-300" :
        "bg-white border-slate-200 hover:border-blue-200 hover:bg-slate-50"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ALLOWED_EXTS.join(",")}
        onChange={onPick}
      />
      {file ? (
        <div className="flex items-center gap-3 justify-center min-w-0">
          {meta ? <meta.icon className={`w-8 h-8 shrink-0 ${meta.color}`} />
                : <FileText className="w-8 h-8 shrink-0 text-slate-400" />}
          <div className="text-left min-w-0">
            <p className="font-medium text-slate-800 truncate" title={file.name}>{file.name}</p>
            <p className="text-xs text-slate-500">
              {fmtSize(file.size)} · {meta?.label || suffix(file.name).toUpperCase().slice(1)}
            </p>
          </div>
          {!busy && (
            <Button
              variant="ghost" size="sm"
              onClick={(e) => { e.stopPropagation(); onSelect(null); }}
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ) : (
        <>
          <UploadIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-700">
            Drop a tender file here, or <span className="text-blue-600 underline">browse</span>.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {ALLOWED_EXTS.join(", ")} · up to {MAX_BYTES / 1024 / 1024} MB
          </p>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

const STAGE = {
  IDLE: "idle",
  UPLOADING: "uploading",
  ANALYZING: "analyzing",  // file is on the server; analyse_rfp running inline
  DONE: "done",
  FAILED: "failed",
};


export default function Upload() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  // Initial projectId honours ?project=<id> so the 3-step wizard's
  // "Done" → /upload?project=<id> round-trip lands here with the
  // freshly-created project already selected.
  const [projectId, setProjectId] = useState(
    () => searchParams.get("project") || null,
  );
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("rfp");
  const [stage, setStage] = useState(STAGE.IDLE);
  const [uploadPct, setUploadPct] = useState(0);
  const [recent, setRecent] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  // F1: guard against a double-click landing two POSTs before React re-renders
  // and disables the button. The ref flips synchronously inside upload();
  // canUpload's stage gate handles the post-render case.
  const submittingRef = useRef(false);

  // ---- bootstrap ----

  const loadProjects = useCallback(async () => {
    try {
      const data = await v2Api.projects.list();
      // Hide CLOSED projects from the upload picker — they shouldn't
      // accept new RFPs. Workspace admins can re-open via project detail.
      const list = (Array.isArray(data) ? data : []).filter(
        (p) => (p.status || "").toLowerCase() !== "closed"
      );
      setProjects(list);
    } catch (err) {
      setLoadError(err?.response?.data?.detail ?? "Could not load projects");
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const data = await v2Api.rfp.list();
      setRecent(Array.isArray(data) ? data.slice(0, 5) : []);
    } catch {
      // Recent uploads are advisory; don't block the page.
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Load project members whenever the selected project changes so we can
  // determine if a manager has explicit access to this project.
  useEffect(() => {
    if (!projectId) { setProjectMembers([]); return; }
    v2Api.projects.members(projectId)
      .then((data) => setProjectMembers(Array.isArray(data) ? data : []))
      .catch(() => setProjectMembers([]));
  }, [projectId]);

  // ---- helpers ----

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId) || null,
    [projects, projectId],
  );

  // Access gate: admin | project lead | manager with explicit project membership
  const canUploadForProject = useMemo(() => {
    if (!projectId) return true; // no project selected yet — don't block early
    if (isAdmin()) return true;
    if (selectedProject?.project_lead_user_id && user?.id === selectedProject.project_lead_user_id) return true;
    if (user?.role === "manager" && projectMembers.some((m) => m.user_id === user?.id && m.is_active !== false)) return true;
    return false;
  }, [isAdmin, user, selectedProject, projectMembers, projectId]);

  const canUpload = !!(file && projectId) && stage === STAGE.IDLE && canUploadForProject;

  // ---- handlers ----

  const handleFile = (f) => {
    if (!f) { setFile(null); return; }
    if (!isAcceptable(f)) {
      toast.error(`Rejected: ${rejectionReason(f)}`);
      return;
    }
    setFile(f);
    // Pre-fill title from filename (without extension) — easy to override.
    if (!title) {
      const base = f.name.replace(/\.[^.]+$/, "");
      setTitle(base.slice(0, 200));
    }
  };

  const createProject = async (payload) => {
    const created = await v2Api.projects.create(payload);
    // Refresh local list so the picker reflects it.
    await loadProjects();
    return created;
  };

  const upload = async () => {
    if (!file || !projectId) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setStage(STAGE.UPLOADING);
    setUploadPct(0);

    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", projectId);
    fd.append("title", (title || file.name).slice(0, 400));
    fd.append("doc_type", docType);
    fd.append("analyze_now", "true");

    try {
      const data = await v2Api.rfp.upload(fd, (e) => {
        if (!e.total) return;
        const pct = Math.round((e.loaded * 100) / e.total);
        setUploadPct(pct);
        if (pct >= 100) setStage(STAGE.ANALYZING);
      });
      // The response shape is RfpOut — parse_status reflects the inline
      // analysis outcome (ANALYZED / FAILED). We don't crash on the
      // FAILED case; we surface it.
      const status = (data.parse_status || "").toLowerCase();
      if (status === "failed") {
        setStage(STAGE.FAILED);
        toast.error(`Analysis failed: ${data.parse_error || "see server logs"}`);
        await loadRecent();
        return;
      }
      setStage(STAGE.DONE);
      toast.success(`Uploaded · ${data.title} → ${status || "ready"}`);
      await loadRecent();
      // Navigate into the new Tender workspace.
      navigate(`/tender/${data.id}`);
    } catch (err) {
      setStage(STAGE.FAILED);
      const msg = err?.response?.data?.detail ?? err.message ?? "Upload failed";
      toast.error(msg);
    } finally {
      submittingRef.current = false;
    }
  };

  const reset = () => {
    setFile(null);
    setTitle("");
    setUploadPct(0);
    setStage(STAGE.IDLE);
  };

  // ---- render ----

  const busy = stage === STAGE.UPLOADING || stage === STAGE.ANALYZING;

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <UploadIcon className="w-5 h-5 text-blue-600" /> Upload tender
        </h1>
        <p className="text-sm text-slate-500">
          Picks up the v2 stack (Postgres + pgvector). Analysis runs inline;
          you'll land on the Tender workspace as soon as it finishes.
        </p>
      </div>

      {loadError && (
        <Card><CardContent className="py-3 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {loadError}
        </CardContent></Card>
      )}

      <ProjectPicker
        projects={projects}
        value={projectId}
        onChange={setProjectId}
        onCreateProject={createProject}
        busy={busy}
        isAdmin={isAdmin}
      />

      {projectId && !canUploadForProject && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-2">
          You don't have upload access to this project. Only the project lead, admins, or managers with project access can upload tenders here.
        </p>
      )}

      <FileDropZone file={file} onSelect={handleFile} busy={busy} />

      {file && (
        <Card>
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-12 gap-3 text-sm">
              <div className="col-span-8">
                <label className="text-xs text-slate-500">Title</label>
                <Input value={title}
                       onChange={(e) => setTitle(e.target.value)}
                       placeholder="Defaults to the filename"
                       disabled={busy} />
              </div>
              <div className="col-span-4">
                <label className="text-xs text-slate-500">Document type</label>
                <select value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        disabled={busy}
                        className="w-full px-3 py-2 border rounded-md bg-white text-sm">
                  <option value="rfp">RFP / NIT</option>
                  <option value="rfq">RFQ</option>
                  <option value="corrigendum">Corrigendum</option>
                  <option value="addendum">Addendum</option>
                </select>
              </div>
            </div>

            {stage === STAGE.UPLOADING && (
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Uploading…</span><span>{uploadPct}%</span>
                </div>
                <Progress value={uploadPct} />
              </div>
            )}
            {stage === STAGE.ANALYZING && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                Analysing the document on the server (15-90s for a typical RFP)…
              </div>
            )}
            {stage === STAGE.FAILED && (
              <div className="flex items-center gap-2 text-sm text-rose-700">
                <AlertCircle className="w-4 h-4" /> Failed — fix the issue and try again.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={reset} disabled={busy}>
                Clear
              </Button>
              <Button onClick={upload} disabled={!canUpload}>
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                {busy ? (stage === STAGE.UPLOADING ? "Uploading…" : "Analysing…") : "Upload & analyse"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">
            Recent uploads
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {recent.length === 0 ? (
            <p className="text-xs italic text-slate-400">No uploads yet.</p>
          ) : (
            <ul className="divide-y">
              {recent.map((r) => {
                const status = (r.parse_status || "").toLowerCase();
                const colour =
                  status === "analyzed" ? "bg-emerald-100 text-emerald-700" :
                  status === "failed"   ? "bg-rose-100 text-rose-700"       :
                                           "bg-amber-100 text-amber-700";
                return (
                  <li key={r.id}
                      className="py-2 flex items-center justify-between gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => navigate(`/tender/${r.id}`)}
                      className="min-w-0 flex-1 text-left hover:underline"
                    >
                      <p className="text-slate-800 truncate">{r.title}</p>
                      <p className="text-xs text-slate-500">
                        {r.pages != null ? `${r.pages} pages · ` : ""}
                        {fmtSize(r.file_size_bytes)} · {fmtRelative(r.created_at)}
                      </p>
                    </button>
                    <Badge className={colour}>{status || "unknown"}</Badge>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => navigate(`/tender/${r.id}`)}
                      title="Open"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
